import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { MessageCounts } from 'src/domain/analytics';

export type MessageDirection = 'all' | 'organization' | 'relative';
export const messageTotal = (
    counts: MessageCounts,
    direction: MessageDirection = 'all',
) =>
    direction === 'all'
        ? counts.organization + counts.relative + counts.other
        : counts[direction];

export const MessageTrend = ({
    trend,
    direction,
    month,
    isAllTime = false,
    onSelectMonth,
}: {
    trend: ({ month: string } & MessageCounts)[];
    direction: MessageDirection;
    month: string;
    isAllTime?: boolean;
    onSelectMonth?: (month: string) => void;
}) => {
    const { t, i18n } = useTranslation();
    const svg = useRef<SVGSVGElement>(null);
    const [viewHeight, setViewHeight] = useState(215);
    useEffect(() => {
        const element = svg.current;
        if (!element || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => {
            if (entry.contentRect.width && entry.contentRect.height)
                setViewHeight(
                    Math.max(
                        215,
                        (entry.contentRect.height * 660) /
                            entry.contentRect.width,
                    ),
                );
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    const highest = Math.max(
        1,
        ...trend.map(row => messageTotal(row, direction)),
    );
    const step = Math.max(1, Math.ceil(highest / 4));
    const ceiling = step * 4;
    const left = 42;
    const top = 24;
    const width = 600;
    const height = viewHeight - 55;
    const slot = width / Math.max(1, trend.length);
    const barWidth = Math.min(28, slot * 0.55);
    const monthName = (value: string) =>
        new Date(`${value}-15T12:00:00Z`).toLocaleDateString(i18n.language, {
            month: 'short',
        });

    const monthYear = (value?: string) =>
        value
            ? new Date(`${value}-15T12:00:00Z`).toLocaleDateString(
                  i18n.language,
                  { month: 'short', year: 'numeric' },
              )
            : '';

    return (
        <div className="analytics-trend">
            <div className="analytics-chart-legend" aria-hidden="true">
                <span>
                    {monthYear(trend[0]?.month)} –{' '}
                    {monthYear(trend[trend.length - 1]?.month)}
                </span>
                {!isAllTime && (
                    <span>
                        <i />
                        {t('admin.analytics.selectedMonth')}
                    </span>
                )}
            </div>
            <svg
                className="analytics-chart"
                ref={svg}
                viewBox={`0 0 660 ${viewHeight}`}
                role="group"
                aria-label={t('admin.analytics.trendDescription')}
            >
                {Array.from({ length: 5 }, (_, index) => {
                    const y = top + height - (index * height) / 4;
                    return (
                        <g key={index}>
                            <line
                                x1={left}
                                x2={left + width}
                                y1={y}
                                y2={y}
                                className="analytics-chart-grid"
                            />
                            <text x={left - 8} y={y + 4} textAnchor="end">
                                {(index * step).toLocaleString(i18n.language)}
                            </text>
                        </g>
                    );
                })}
                {trend.map((row, index) => {
                    const count = messageTotal(row, direction);
                    const x = left + slot * (index + 0.5);
                    const barHeight = (count / ceiling) * height;
                    return (
                        <g
                            key={row.month}
                            role={onSelectMonth ? 'button' : undefined}
                            tabIndex={onSelectMonth ? 0 : undefined}
                            aria-label={t('admin.analytics.selectChartMonth', {
                                month: monthYear(row.month),
                                count,
                            })}
                            aria-pressed={!isAllTime && row.month === month}
                            className={
                                onSelectMonth
                                    ? 'analytics-chart-month'
                                    : undefined
                            }
                            onClick={() => onSelectMonth?.(row.month)}
                            onKeyDown={event => {
                                if (
                                    onSelectMonth &&
                                    (event.key === 'Enter' || event.key === ' ')
                                ) {
                                    event.preventDefault();
                                    onSelectMonth(row.month);
                                }
                            }}
                        >
                            <title>{`${row.month}: ${count.toLocaleString(
                                i18n.language,
                            )}`}</title>
                            <rect
                                className="analytics-chart-hit-area"
                                x={x - slot / 2 + 2}
                                y={top - 16}
                                width={slot - 4}
                                height={height + 44}
                                rx={4}
                            />
                            <rect
                                x={x - barWidth / 2}
                                y={top + height - barHeight}
                                width={barWidth}
                                height={barHeight}
                                rx={3}
                                className={
                                    !isAllTime && row.month === month
                                        ? 'analytics-chart-bar-selected'
                                        : 'analytics-chart-bar'
                                }
                            />
                            <text
                                x={x}
                                y={top + height - barHeight - 7}
                                textAnchor="middle"
                                className="analytics-chart-count"
                            >
                                {count.toLocaleString(i18n.language)}
                            </text>
                            <text
                                x={x}
                                y={top + height + 23}
                                textAnchor="middle"
                            >
                                {monthName(row.month)}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <div className="sr-only">
                <table>
                    <caption>{t('admin.analytics.trendDescription')}</caption>
                    <thead>
                        <tr>
                            <th>{t('admin.analytics.reportingMonth')}</th>
                            <th>{t('admin.analytics.originalMessages')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trend.map(row => (
                            <tr key={row.month}>
                                <th scope="row">{row.month}</th>
                                <td>{messageTotal(row, direction)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
